import { FC, useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { ProgressStudentsRoadmapByGradeQuery } from '../../../graphQLOperations/__generated__/ProgressStudentsRoadmapByGradeQuery.graphql';
import { getStudentRoadmapByGrade } from '../../../graphQLOperations/Progress';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { findChipColors } from '../../../businessLogicService/getColorsForProgressTableChip';
import { selectedReportsFilter } from '../../../redux/reducers/filterReportsSlice';
import { useAppSelector } from '../../../redux/hooks';
import ProgressColumnName from './ProgressColumnName';
import { StudentNameLink } from '../../common/StudentNameLink/StudentNameLink';
import { MaskedStudentData } from '../../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../../analytics/dataMasking';

interface Props {
  roadmapQueryRef: PreloadedQuery<ProgressStudentsRoadmapByGradeQuery>;
}
export interface RoadmapTableValues {
  studentLastName: string;
  studentFirstName: string;
  studentId: string;
  studentPublicUserId: string;
  currentRoadmap: string | null;
  currentRoadmapName?: string | null;
  [key: `gradeName${number}`]: string;
}

const RoadmapsProgressByGradeTable: FC<Props> = ({ roadmapQueryRef }) => {
  const data = usePreloadedQuery(getStudentRoadmapByGrade, roadmapQueryRef);
  const [values, setValues] = useState<RoadmapTableValues[]>([]);
  const [columns, setColumns] = useState<{ field: string; header: string }[]>([]);
  const reportsFilterRedux = useAppSelector(selectedReportsFilter);

  useEffect(() => {
    if (!data?.studentRoadmapProgressByGrade?.length) return;

    const gradeColumns = data.studentRoadmapProgressByGrade
      .reduce((existingGradeNames: string[], studentProgress) => {
        studentProgress.gradeSummaries
          .map(gs => gs.gradeName)
          .forEach(gradeName => {
            if (existingGradeNames.indexOf(gradeName) === -1) {
              existingGradeNames.push(gradeName);
            }
          });
        return existingGradeNames;
      }, [])
      .sort();
    let columnData = [{ field: 'name', header: 'Name' }];
    columnData = [
      ...columnData,
      ...gradeColumns.map(el => ({
        field: el,
        header: el,
      })),
    ];
    setColumns(columnData);

    const valuesData = data.studentRoadmapProgressByGrade.map(el => {
      const row: RoadmapTableValues = {
        studentFirstName: el.studentFirstName,
        studentLastName: el.studentLastName,
        studentId: String(el.studentId),
        studentPublicUserId: el.studentPublicUserId,
        currentRoadmap: el.currentRoadmapId ? (String(el.currentRoadmapId) as string) : (el.currentRoadmapId as null),
        currentRoadmapName: el.currentRoadmapName,
      };

      el.gradeSummaries.forEach(el => {
        row[el.gradeName as keyof RoadmapTableValues] = `${el.demonstrated}/${el.total}`;
      });

      return row;
    });
    setValues(valuesData);
  }, [data]);

  const columnBodyChip = (data: RoadmapTableValues, field: string) => {
    const values = ((data[field as keyof RoadmapTableValues] as string) ?? '0/0').split('/');
    const colorChipClassName = findChipColors((parseInt(values[0]) / parseInt(values[1])) * 100);
    return (
      <div className="flex justify-content-center">
        <span className={`nc_progress-roadmap-table-chip ${colorChipClassName}`}>
          {data[field as keyof RoadmapTableValues]}
        </span>
      </div>
    );
  };

  const headerColumnGroupTemplate = (
    <ColumnGroup>
      <Row>
        <Column colSpan={2} />
        <Column
          colSpan={columns.length - 1}
          header="Demonstrated Skills"
          style={{ borderBottom: '3px solid #008158', textTransform: 'uppercase' }}
        />
      </Row>
      <Row>
        <Column field="studentFirstName" header="First Name" sortable />
        <Column field="studentLastName" header="Last Name" sortable />
        {columns.map((column, index) => {
          if (index === 0) return null;
          return <Column key={index} field={column.field} header={column.header} sortable />;
        })}
      </Row>
    </ColumnGroup>
  );

  return (
    <DataTable
      sortField="studentLastName"
      sortOrder={1}
      stripedRows
      totalRecords={values.length}
      rows={10}
      rowsPerPageOptions={[5, 10, 25]}
      className="nc_progress-table"
      paginator
      paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      value={values}
      headerColumnGroup={headerColumnGroupTemplate}
    >
      <Column
        field="studentFirstName"
        header="First Name"
        body={data => (
          <div style={{ fontFamily: 'Poppins' }}>
            <StudentNameLink studentId={String(data.studentId)} classIds={reportsFilterRedux.classes.map(cl => cl.id)}>
              <MaskedStudentData student={{ publicUserId: data.studentPublicUserId }} type={PiiTypes.FirstName}>
                {data.studentFirstName}
              </MaskedStudentData>
            </StudentNameLink>
          </div>
        )}
        sortable
        style={{ minWidth: '180px' }}
      />
      <Column
        field="studentLastName"
        header="Last Name"
        body={(data: RoadmapTableValues) => {
          return <ProgressColumnName studentData={data} filterRoadmapId={reportsFilterRedux.roadmaps?.id as string} />;
        }}
        sortable
        style={{ minWidth: '180px' }}
      />
      {columns.map((column, index) => {
        if (index === 0) return null;
        return (
          <Column
            key={index}
            field={column.field}
            showFilterMatchModes={false}
            header={column.header}
            sortable
            body={data => columnBodyChip(data, column.field)}
          />
        );
      })}
    </DataTable>
  );
};

export default RoadmapsProgressByGradeTable;
